import { useState } from 'react'
import { motion } from 'framer-motion'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import Button from '@/components/common/Button/Button'
import PasswordInput from '@components/auth/PasswordInput/PasswordInput'
import Logo from '@components/common/Logo/Logo'

import MotionText from '@/components/common/Motion/MotionText'
import { usePageSetup } from '@/hooks/usePageSetup'

import passwordsAPI from '@/services/api/passwords'

export const PasswordResetPage = () => {
  const { t } = useTranslation()
  usePageSetup(t('auth.pageTitle.passwordReset'), { scroll: true, noindex: true })

  const [isLoading, setIsLoading] = useState(false)

  const navigate = useNavigate()
  const location = useLocation()
  const token = location.state?.token

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const formData = new FormData(e.currentTarget)
    const password = formData.get('password') as string

    try {
      setIsLoading(true)

      await passwordsAPI.reset({
        token,
        password: password,
        password_confirmation: password
      })

      navigate("/users/login")
    } catch (error: any) {
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="h-full bg-white p-5 pt-8 pb-8 flex flex-col">
      <div className="space-y-4">
        <Logo />
      </div>

      <div className="flex-1 flex flex-col">
        <div className='h-[2rem] w-1'></div>
        <div className="space-y-4">
          <MotionText
            as="h2"
            className="text-zinc-900 text-3xl font-bold leading-tight"
            delay={0.2}
          >
            {t('auth.newPassword')}
          </MotionText>
          <MotionText
            as="p"
            className="text-zinc-700 text-base"
            delay={0.4}
          >
            {t('auth.newPasswordDescription')}
          </MotionText>
        </div>
        <motion.div className='my-6'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: 0.3,
            ease: "easeInOut"
          }}
        >
          <PasswordInput
            required
            name='password'
            label={t('auth.yourNewPassword')}
            autoComplete='new-password'
          />
        </motion.div>
      </div>

      <div className="flex flex-row gap-4">
        <Button type='submit' isLoading={isLoading}>
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: 0.2,
              ease: "easeInOut"
            }}
          >
            {t('common.save')}
          </motion.span>
        </Button>
      </div>
    </form>
  )
}

export default PasswordResetPage